import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Users, Bed, Bath, Star, Heart } from 'lucide-react';

const VillaCard = ({ villa, onViewDetails, onToggleFavorite, isFavorite = false, availabilityStatus }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Dégradé élégant et neutre pour toutes les villas
  const cardStyle = {
    background: 'linear-gradient(135deg, #FEFEFE 0%, #F8F6F3 50%, #E8F2F7 100%)',
    transform: isHovered ? 'translateY(-12px) rotateX(2deg)' : 'translateY(0) rotateX(0)',
    boxShadow: isHovered 
      ? `0 20px 40px rgba(212, 175, 55, 0.3), 0 15px 12px rgba(0, 0, 0, 0.1)`
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const overlayStyle = {
    background: isHovered 
      ? 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.7) 100%)'
      : 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)',
    transition: 'background 0.3s ease'
  };

  return (
    <Card 
      className="relative overflow-hidden cursor-pointer group border-0 shadow-none"
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onViewDetails(villa)}
    >
      {/* Main Image Container */}
      <div className="relative h-80 overflow-hidden">
        <img 
          src={villa.mainImage}
          alt={villa.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out"
          style={{
            transform: isHovered ? 'scale(1.05)' : 'scale(1.02)',
          }}
        />
        
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0"
          style={overlayStyle}
        />

        {/* Favorite Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(villa.id);
          }}
          className="absolute top-4 right-4 p-2 rounded-full backdrop-blur-sm bg-white/20 hover:bg-white/30 transition-all duration-300"
        >
          <Heart 
            className={`w-5 h-5 transition-colors duration-300 ${
              isFavorite ? 'text-red-500 fill-red-500' : 'text-white'
            }`}
          />
        </button>

        {/* Property Type Badge */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <Badge 
            variant="secondary" 
            className="backdrop-blur-md bg-white/15 text-white border-white/20 px-3 py-1 text-xs font-medium uppercase tracking-wide"
          >
            {villa.propertyType || 'Villa'}
          </Badge>
          
          {/* Availability Badge */}
          {availabilityStatus && (
            <Badge 
              variant="secondary" 
              className={`
                backdrop-blur-md px-3 py-1 text-xs font-medium uppercase tracking-wide
                ${availabilityStatus === 'available' 
                  ? 'bg-green-500/90 text-white border-green-400/30' 
                  : availabilityStatus === 'unavailable'
                  ? 'bg-red-500/90 text-white border-red-400/30'
                  : 'bg-gray-500/90 text-white border-gray-400/30'
                }
              `}
            >
              {availabilityStatus === 'available' 
                ? '✓ Disponible' 
                : availabilityStatus === 'unavailable'
                ? '✗ Non disponible'
                : '? À vérifier'
              }
            </Badge>
          )}
        </div>

        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          {/* Location */}
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-light tracking-wide">{villa.location}</span>
          </div>

          {/* Villa Name & Tagline */}
          <h3 className="text-2xl font-light mb-2 tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
            {villa.name}
          </h3>
          <p className="text-sm opacity-90 mb-4 italic font-light">
            {villa.emotionalTagline}
          </p>

        </div>
      </div>

      {/* Bottom Section */}
      <div className="p-6 bg-white/95 backdrop-blur-sm">
        {/* Story Preview */}
        <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-2">
          {villa.story}
        </p>

        {/* Price and Details */}
        <div>
          {/* Zone */}
          {villa.zone && (
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {villa.zone}
            </div>
          )}
          
          {/* Price */}
          <div className="text-2xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {villa.priceDisplay}
          </div>
          
          {/* Subtext and Villa Details on same line */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">{villa.priceSubtext}</div>
            
            {/* Villa Details (moved from overlay) */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{villa.capacity}</span>
              </div>
              <div className="flex items-center gap-1">
                <Bed className="w-4 h-4" />
                <span>{villa.bedrooms}</span>
              </div>
              <div className="flex items-center gap-1">
                <Bath className="w-4 h-4" />
                <span>{villa.bathrooms}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hover Ripple Effect */}
      {isHovered && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(212, 175, 55, 0.1) 0%, transparent 70%)',
            animation: 'ripple 0.6s ease-out'
          }}
        />
      )}
    </Card>
  );
};

export default VillaCard;